import { useState, useCallback, useEffect } from 'react';
import { createHasSomeMonth } from '../../utils/planner';

const usePlannerFetcher = (fetcher: (months: Date[]) => Promise<void>, loadingLazy?: boolean) => {
    const [loadedMonths, setLoadedMonths] = useState<Date[] | null>(null);
    const [loading, setLoading] = useState(false);
    // months requested while we were busy, these get loaded as soon as the current fetch is done
    const [pendingMonths, setPendingMonths] = useState<Date[] | null>(null);
    const isLoaded = useCallback(
        (month: Date) => {
            if (!loadedMonths) {
                return false;
            }

            return createHasSomeMonth(loadedMonths)(month);
        },
        [loadedMonths]
    );

    const loadMore = useCallback(
        async (months: Date[]) => {
            if (loadingLazy) {
                return;
            }
            if (loading) {
                setPendingMonths(months);
                return;
            }
            // we only want to fetch months that aren't loaded yet
            const corrected = months.filter(month => !isLoaded(month));

            if (!corrected.length) {
                return;
            }
            setLoading(true);

            await fetcher(corrected);

            setLoadedMonths(current => [...(current ?? []), ...months]);
            setLoading(false);
        },
        [fetcher, isLoaded, loading, loadingLazy]
    );

    useEffect(() => {
        if (pendingMonths && !loading) {
            setPendingMonths(null);
            loadMore(pendingMonths);
        }
    }, [loadMore, loading, pendingMonths]);

    const invalidate = useCallback(() => setLoadedMonths(null), []);

    return [
        { loadedMonths, loading },
        { isLoaded, invalidate, loadMore },
    ] as const;
};

export default usePlannerFetcher;
